import { useState } from "react";
import { Box, Card, CardContent, Chip, Collapse, IconButton, Typography } from "@mui/material";
import { ExpandMore, ExpandLess, Build } from "@mui/icons-material";
import type { ConversationMessage, ToolInvocationStatus } from "@/types";

const STATUS_COLORS: Record<
  ToolInvocationStatus,
  "default" | "primary" | "success" | "error" | "warning"
> = {
  pending: "warning",
  executing: "primary",
  succeeded: "success",
  failed: "error",
  rejected: "default",
  scope_missing: "error",
};

const STATUS_LABELS: Record<ToolInvocationStatus, string> = {
  pending: "Pending approval",
  executing: "Running",
  succeeded: "Done",
  failed: "Failed",
  rejected: "Rejected",
  scope_missing: "Missing scope",
};

interface ToolCallCardProps {
  message: ConversationMessage;
  result?: ConversationMessage; // The matching TOOL message that produced the result, if any
}

export default function ToolCallCard({ message, result }: ToolCallCardProps) {
  const [expanded, setExpanded] = useState(false);

  const status = message.tool_invocation_status ?? result?.tool_invocation_status ?? null;

  return (
    <Card variant="outlined" sx={{ my: 0.5 }}>
      <CardContent sx={{ py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Build fontSize="small" color="action" />
          <Typography variant="body2" sx={{ fontFamily: "monospace", flex: 1 }}>
            {message.tool_name}
          </Typography>
          {status && (
            <Chip
              label={STATUS_LABELS[status]}
              size="small"
              color={STATUS_COLORS[status]}
              variant="outlined"
            />
          )}
          <IconButton size="small" onClick={() => setExpanded((e) => !e)}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Box>
        <Collapse in={expanded}>
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Arguments
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                bgcolor: "background.default",
                borderRadius: 1,
                fontSize: "0.75rem",
                overflow: "auto",
                maxHeight: 300,
              }}
            >
              {JSON.stringify(message.tool_arguments ?? {}, null, 2)}
            </Box>
            {result?.tool_result && (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Result
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    overflow: "auto",
                    maxHeight: 300,
                  }}
                >
                  {JSON.stringify(result.tool_result, null, 2)}
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}
